import React, { useEffect, useState, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import "../../styles/selection-topbar.css";
import "../../styles/company-selection.css";
import "../../components/header/styles.css";
import { Building2, ArrowRight, Settings, Search, AlertCircle, LogOut, Mail, Phone, User, Calendar, BookOpen } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../components/ThemeContext";
import backendApi from "../../services/backendApi";
import UserMenu from "../../components/header/UserMenu";

interface CompanyItem {
  company_id: number | string;
  company_name: string;
  company_code?: string;
  company_slug?: string;
  industry_type?: string | null;
  country?: string | null;
  location?: string | null;
  timezone?: string;
  currency?: string;
  description?: string | null;
  portfolio_size?: number | null;
  plant_count?: number | null;
  primary_contact_name?: string | null;
  primary_contact_email?: string | null;
  primary_contact_phone?: string | null;
  primary_contact_phones?: string[];
  subscription_plan?: string;
  max_sites?: number;
  max_users?: number;
  is_active?: boolean;
  is_trial?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
  logo_url?: string | null;
}

const SuperAdminCompanySelection: React.FC = () => {
  const navigate = useNavigate();
  const { user, login, logout } = useAuth();
  const { isDarkMode } = useTheme();
  const [companies, setCompanies] = useState<CompanyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!userMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        const portal = document.querySelector('.user-dropdown-menu');
        if (portal && portal.contains(e.target as Node)) return;
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [userMenuOpen]);

  const filteredCompanies = useMemo(() => {
    return companies.filter(company =>
      company.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      company.company_code?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [companies, searchQuery]);


  useEffect(() => {
    if (!user) return;
    const roleName = (user.roleName || user.role || "").toLowerCase();
    const isSystemAdmin =
      !!user.isGlobalAdmin || roleName === "system_admin" || roleName === "super_admin";

    if (!isSystemAdmin) {
      navigate("/select-site", { replace: true });
    }
  }, [user, navigate]);

  const companiesRef = React.useRef(companies);
  useEffect(() => {
    companiesRef.current = companies;
  }, [companies]);

  const fetchCompanies = async (showLoadingState = true) => {
    try {
      if (showLoadingState) {
        setLoading(true);
        setError("");
      }
      const response = await backendApi.getCompanies();
      if (response.success && Array.isArray(response.companies)) {
        setCompanies(response.companies);
        setError("");
      } else {
        if (showLoadingState || companiesRef.current.length === 0) {
          setError("Failed to load onboarded companies.");
        }
      }
    } catch (err) {
      if (showLoadingState || companiesRef.current.length === 0) {
        setError("Failed to load onboarded companies.");
      }
    } finally {
      if (showLoadingState) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    // Initial fetch with full loading state
    fetchCompanies(true);

    // Dynamic background refresh every 5 seconds without showing loading spinner/skeleton
    const intervalId = setInterval(() => {
      fetchCompanies(false);
    }, 5000);

    return () => clearInterval(intervalId);
  }, []);


  const handleCompanySelect = (company: CompanyItem) => {
    const companyId = String(company.company_id);

    sessionStorage.setItem("company_id", companyId);
    localStorage.setItem("company_id", companyId);
    sessionStorage.setItem("selected_company_name", company.company_name || "");
    localStorage.setItem("selected_company_name", company.company_name || "");
    sessionStorage.removeItem("site_id");
    localStorage.removeItem("site_id");
    sessionStorage.removeItem("tenant");
    localStorage.removeItem("currentSite");

    if (user) {
      login({
        ...user,
        companyId: companyId,
        company: company.company_name || user.company,
        siteId: null,
        siteCode: null,
        tenant: "",
      });
    }

    navigate(`/select-site?company_id=${encodeURIComponent(companyId)}`);
  };

  const shell = `axc ibm-plex-page${isDarkMode ? ' is-dark' : ''}`;

  if (loading) {
    return (
      <div className={shell}>
        <div className="axc-pagehead">
          <div className="axc-container">
            <div className="axc-skel" style={{ height: 13, width: 150 }} />
            <div className="axc-skel" style={{ height: 34, width: 380, marginTop: 18 }} />
            <div className="axc-skel" style={{ height: 15, width: 460, marginTop: 16 }} />
            <div className="axc-skel" style={{ height: 43, width: 420, marginTop: 28 }} />
          </div>
        </div>
        <div className="axc-main">
          <div className="axc-container axc-body">
            <div className="axc-grid">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="axc-card" style={{ cursor: 'default' }}>
                  <div className="axc-card__head">
                    <div className="axc-skel" style={{ width: 44, height: 44 }} />
                    <div>
                      <div className="axc-skel" style={{ height: 15, width: '70%' }} />
                      <div className="axc-skel" style={{ height: 11, width: '40%', marginTop: 8 }} />
                    </div>
                    <div />
                  </div>
                  <div style={{ padding: '0 20px 18px' }}>
                    <div className="axc-skel" style={{ height: 12, width: '100%' }} />
                    <div className="axc-skel" style={{ height: 12, width: '80%', marginTop: 7 }} />
                  </div>
                  <div className="axc-metrics">
                    <div className="axc-metric"><div className="axc-skel" style={{ height: 34 }} /></div>
                    <div className="axc-metric"><div className="axc-skel" style={{ height: 34 }} /></div>
                  </div>
                  <div className="axc-contact">
                    <div className="axc-skel" style={{ height: 12, width: '60%' }} />
                    <div className="axc-skel" style={{ height: 12, width: '75%' }} />
                  </div>
                  <div className="axc-card__foot">
                    <div className="axc-skel" style={{ height: 11, width: 130 }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const initials = (name?: string) =>
    name?.split(' ').slice(0, 2).map((w: string) => w[0]).join('').toUpperCase() || 'CO';

  const phonesOf = (c: CompanyItem) =>
    c.primary_contact_phones && c.primary_contact_phones.length > 0
      ? c.primary_contact_phones
      : c.primary_contact_phone
        ? c.primary_contact_phone.split(',').map((x: string) => x.trim()).filter(Boolean)
        : [];

  return (
    <div className={shell}>
      {/* Top bar */}
      <header className={`sticky top-0 z-40 border-b transition-colors ${isDarkMode ? 'bg-[#12263F] border-[#1C4D88]' : 'bg-white border-[#DFE6EE]'} shadow-sm`}>
        <div className="w-full px-4 sm:px-8 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="/Axon-logo.png"
              alt="AxonMetric Logo"
              className="h-8 sm:h-10 hover:scale-105 transition-transform duration-200 cursor-pointer"
              onClick={() => navigate('/')}
            />
          </div>

          <div className="axm-topbar flex items-center gap-2.5">
            <button
              type="button"
              onClick={() => navigate('/guide')}
              className={`axm-topbtn inline-flex items-center gap-2 px-3.5 py-2 rounded-lg transition-colors text-sm border ${isDarkMode
                ? 'border-[#2F5C8A] bg-transparent hover:bg-[#0084CF]/15 hover:border-[#0084CF] text-[#C2DFF1]'
                : 'border-[#C5D0DC] bg-white hover:bg-[#F2F8FC] hover:border-[#0084CF] text-[#1C4D88]'
                }`}
              title="Where to go and what to click"
            >
              <BookOpen size={16} strokeWidth={1.8} aria-hidden="true" />
              <span>Guide</span>
            </button>
            <button
              onClick={() => navigate('/admin')}
              className={`axm-topbtn inline-flex items-center gap-2 px-3.5 py-2 rounded-lg transition-colors text-sm border ${isDarkMode
                ? 'border-[#2F5C8A] bg-transparent hover:bg-[#0084CF]/15 hover:border-[#0084CF] text-[#C2DFF1]'
                : 'border-[#C5D0DC] bg-white hover:bg-[#F2F8FC] hover:border-[#0084CF] text-[#1C4D88]'
                }`}
              title="Go to Admin Panel"
            >
              <Settings size={16} strokeWidth={1.8} />
              <span>Admin</span>
            </button>


            <div ref={userMenuRef} style={{ position: 'relative', zIndex: 10001 }}>
              <UserMenu
                displayName={user?.fullName || user?.username || 'User'}
                displayRole={user?.roleName || user?.role || 'Super Admin'}
                site=""
                showSiteNavigation={false}
                menuOpen={userMenuOpen}
                onToggleMenu={() => setUserMenuOpen(v => !v)}
                onSiteInfo={() => {
                  setUserMenuOpen(false);
                  navigate('/select-company');
                }}
                onLogout={() => {
                  setUserMenuOpen(false);
                  logout?.();
                  navigate('/login');
                }}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Page header */}
      <div className="axc-pagehead">
        <div className="axc-container">
          <div className="axc-pagehead__grid">
            <div>
              <span className="axc-eyebrow">
                <Building2 size={12} strokeWidth={2} />
                Super admin access
              </span>
              <h1 className="axc-h1">
                Select an <em>enterprise organization</em>
              </h1>
              <p className="axc-lede">
                Select a company to access its clean energy power plants, view live
                generation metrics, and access operational dashboards.
              </p>
            </div>

            {!error && companies.length > 0 && (
              <div className="axc-count">
                <span className="axc-count__value">{filteredCompanies.length}</span>
                <span className="axc-count__label">
                  {searchQuery
                    ? `of ${companies.length} matching`
                    : `Organization${companies.length === 1 ? '' : 's'}`}
                </span>
              </div>
            )}
          </div>

          {!error && companies.length > 0 && (
            <div className="axc-search">
              <Search />
              <input
                type="search"
                aria-label="Search onboarded companies"
                placeholder="Search onboarded companies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button className="axc-search__clear" onClick={() => setSearchQuery("")}>
                  Clear
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <main className="axc-main">
        <div className="axc-container axc-body">

          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className="axc-alert"
              >
                <AlertCircle size={19} strokeWidth={1.8} />
                <div>
                  <div className="axc-alert__title">Failed to load companies</div>
                  <div className="axc-alert__text">{error}</div>
                  <button
                    className="axc-btn axc-btn--quiet"
                    style={{ marginTop: 14 }}
                    onClick={() => { setError(""); fetchCompanies(); }}
                  >
                    Try again
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {!error && companies.length === 0 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="axc-state">
              <span className="axc-state__icon"><Building2 size={24} strokeWidth={1.5} /></span>
              <div className="axc-state__title">No companies onboarded</div>
              <p className="axc-state__text">
                No companies have been onboarded yet. Please contact your system administrator.
              </p>
            </motion.div>
          )}

          {!error && companies.length > 0 && filteredCompanies.length === 0 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="axc-state">
              <span className="axc-state__icon"><Search size={24} strokeWidth={1.5} /></span>
              <div className="axc-state__title">No companies match your search</div>
              <p className="axc-state__text">
                Nothing matched &ldquo;{searchQuery}&rdquo;. Try a different name or company code.
              </p>
              <button className="axc-btn" onClick={() => setSearchQuery("")}>Clear search</button>
            </motion.div>
          )}

          {!error && filteredCompanies.length > 0 && (
            <div className="axc-grid">
              <AnimatePresence>
                {filteredCompanies.map((company, index) => {
                  const phones = phonesOf(company);
                  const busy = selectedCompany === String(company.company_id);
                  return (
                    <motion.button
                      key={String(company.company_id)}
                      type="button"
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -12 }}
                      transition={{ delay: Math.min(index, 8) * 0.03 }}
                      onClick={() => {
                        setSelectedCompany(String(company.company_id));
                        handleCompanySelect(company);
                      }}
                      className="axc-card"
                    >
                      {/* Header */}
                      <div className="axc-card__head">
                        {company.logo_url ? (
                          <span className="axc-mark axc-mark--img">
                            <img src={company.logo_url} alt="" />
                          </span>
                        ) : (
                          <span className="axc-mark">{initials(company.company_name)}</span>
                        )}
                        <span style={{ minWidth: 0 }}>
                          <span className="axc-card__name" style={{ display: 'block' }}>
                            {company.company_name || "Unnamed Company"}
                          </span>
                          {company.company_code && (
                            <span className="axc-card__code" style={{ display: 'block' }}>
                              {company.company_code}
                            </span>
                          )}
                        </span>
                        <ArrowRight size={17} strokeWidth={1.8} className="axc-card__go" />
                      </div>

                      {/* Badges */}
                      {(company.industry_type || company.is_trial) && (
                        <div className="axc-badges">
                          {company.industry_type && (
                            <span className="axc-badge">{company.industry_type}</span>
                          )}
                          {company.is_trial && (
                            <span className="axc-badge axc-badge--trial">Trial</span>
                          )}
                        </div>
                      )}

                      {/* Description */}
                      <p className="axc-card__desc">
                        {company.description || `A leading ${company.industry_type?.toLowerCase() || 'renewable energy'} enterprise managing operations${company.location || company.country ? ` in ${company.location || company.country}` : ''}.`}
                      </p>

                      {/* Metrics */}
                      <div className="axc-metrics">
                        <div className="axc-metric">
                          <span className="axc-metric__label">Portfolio size</span>
                          <span className="axc-metric__value">
                            {company.portfolio_size != null && company.portfolio_size > 0
                              ? <>{Number(company.portfolio_size).toLocaleString()}<span className="axc-metric__unit">MW</span></>
                              : '—'}
                          </span>
                        </div>
                        <div className="axc-metric">
                          <span className="axc-metric__label">Plants onboarded</span>
                          <span className="axc-metric__value">
                            {company.plant_count != null
                              ? <>{company.plant_count}<span className="axc-metric__unit">{company.plant_count === 1 ? 'Plant' : 'Plants'}</span></>
                              : '—'}
                          </span>
                        </div>
                      </div>

                      {/* Contact */}
                      <div className="axc-contact">
                        <span className="axc-contact__row axc-contact__row--name">
                          <User strokeWidth={1.8} />
                          <span>
                            {company.primary_contact_name || (
                              <span className="axc-contact__empty">No contact on file</span>
                            )}
                          </span>
                        </span>
                        {company.primary_contact_email && (
                          <span className="axc-contact__row">
                            <Mail strokeWidth={1.8} />
                            <a
                              href={`mailto:${company.primary_contact_email}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {company.primary_contact_email}
                            </a>
                          </span>
                        )}
                        {phones.map((phone: string, idx: number) => (
                          <span className="axc-contact__row" key={idx}>
                            <Phone strokeWidth={1.8} />
                            <a href={`tel:${phone}`} onClick={(e) => e.stopPropagation()}>
                              {phone}
                            </a>
                          </span>
                        ))}
                      </div>

                      {/* Footer */}
                      <div className="axc-card__foot">
                        <span className="axc-card__meta">
                          <Calendar strokeWidth={1.8} />
                          Onboarded&nbsp;
                          <strong>
                            {company.created_at
                              ? new Date(company.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })
                              : 'N/A'}
                          </strong>
                        </span>
                        <span className="axc-card__cta">Open</span>
                      </div>

                      {busy && (
                        <span className="axc-card__busy">
                          <span className="axc-spinner axc-spinner--sm" />
                        </span>
                      )}
                    </motion.button>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SuperAdminCompanySelection;
